import { FunctionComponent, useEffect, useState } from "react";
import { CheckIcon, CloseIcon } from "@form/components/common/icons";
import { twitterUserLink } from "@form/utils/format";
import { Button } from "@mantine/core";
import Link from "next/link";
import useUserTwitter from "@form/hooks/useUserTwitter";
import useVerifyCampaign from "@form/hooks/useVerifyCampaign";
import mixpanel from "mixpanel-browser";
import useAccount from "@/hooks/useAccount";
import { confirm } from "@form/services/modal";

import type { TwitterForm } from "@form/components/form/modules/twitter";
import type { SubmitError } from "@form/hooks/useVerifyCampaign";

const TwitterVerify: FunctionComponent<{
  form: TwitterForm;
  available: boolean;
  needVerify: boolean;
  submitError?: SubmitError;
}> = ({ form, available, needVerify, submitError }) => {
  const { login, isLogin } = useAccount();
  const { twitter, connect, connecting, authLink, disconnect } =
    useUserTwitter();
  const { changeVerifyState, result } = useVerifyCampaign(`twitter`);

  const showConnectError = needVerify && !result.twitter;

  // const {changeVerifyState: changeFollowVerifyState} = useVerifyCampaign(`twitter_follow`);
  // const {changeVerifyState: changeLikeVerifyState} = useVerifyCampaign(`twitter_like`);
  // const {changeVerifyState: changeRetweetVerifyState} = useVerifyCampaign(`twitter_retweet`);

  // const {accountVerified, likeLinkVerified, retweetLinkVerified} = useTwitterVerify(form, twitter);

  // console.log(accountVerified, likeLinkVerified, retweetLinkVerified)

  const isConnected = !!twitter && available;

  useEffect(() => {
    changeVerifyState(isConnected);
  }, [isConnected]);

  // useEffect(() => {
  //   changeFollowVerifyState(!form.values.needFollow || accountVerified);
  // }, [accountVerified, form.values.needFollow])

  // useEffect(() => {
  //   changeLikeVerifyState(!form.values.needLike || likeLinkVerified);
  // }, [likeLinkVerified, form.values.needLike])

  // useEffect(() => {
  //   changeRetweetVerifyState(!form.values.needRetweet || retweetLinkVerified);
  // }, [retweetLinkVerified, form.values.needRetweet])

  let verifyCount = 0;

  if (form.values.needFollow && form.values.accounts) {
    verifyCount += form.values.accounts.length;
  }
  if (form.values.needRetweet && form.values.retweetLink) {
    verifyCount++;
  }
  if (form.values.needLike && form.values.likeLink) {
    verifyCount++;
  }

  const showRetweetError = !!submitError?.[form.values.uid]?.needRetweet;
  const showLikeError = !!submitError?.[form.values.uid]?.needLike;

  return (
    <div className={available ? "" : "relative opacity-40 disable"}>
      <div className="flex items-center">
        <div className="flex-1">
          <div className="flex items-center">
            {form.module.icon && (
              <form.module.icon className="fill-[#78C6F5] w-5"></form.module.icon>
            )}
            <span className="text-body text-dark1 font-bold ml-2 dark:text-gray_f9">
              Twitter
            </span>
            {showConnectError && !twitter && !isConnected && (
              <span className="text-error text-sm ml-2">This is required</span>
            )}
            {twitter && isConnected && (
              <div
                className="px-2 py-1 flex items-center rounded-[35px] text-bodyS text-gray_58 ml-2 dark:text-[#DCECFA]"
                style={{
                  boxShadow: "2px 4px 16px rgba(0, 0, 0, 0.05)",
                }}
              >
                <span>@{twitter.name}</span>
                <div
                  className="action ml-1 w-[14px] h-[14px] rounded-full bg-primary flex items-center justify-center"
                  onClick={() => {
                    confirm({
                      title: "Are you sure to disconnect your account?",
                      okText: "Disconnect",
                      okType: "danger",
                      onOk() {
                        disconnect();
                      },
                    });
                  }}
                >
                  <CloseIcon className="w-[6px]"></CloseIcon>
                </div>
              </div>
            )}
            <div className="flex-1"></div>
            {isConnected && (
              <span className="text-green text-bodyS font-bold dark:text-dark_green">
                Connected
              </span>
            )}
          </div>
        </div>
        {!isConnected && (
          <div className="flex-shrink-0 ml-4">
            <a
              rel="noreferrer"
              href={authLink}
              className="block"
              target="_blank"
              onClick={(e) => {
                if (!isLogin) {
                  login();
                  e.preventDefault();
                  return false;
                }
                if (!authLink) {
                  e.preventDefault();
                  return false;
                }
                mixpanel.track("connect_twitter");
                connect();
              }}
            >
              <Button
                loading={connecting}
                variant="outline"
                radius="xl"
                size="md"
              >
                Connect
              </Button>
            </a>
          </div>
        )}
      </div>
      <div
        className={`mt-2 ${isConnected ? "" : "hidden"} ${
          verifyCount > 1 ? "grid grid-cols-2 gap-2 " : ""
        }`}
      >
        {form.values.needFollow &&
          form.values.accounts &&
          form.values.accounts.map((account, index) => {
            const followError =
              submitError?.[form.values.uid]?.needFollow || [];
            const showError = followError.includes(account);
            return (
              <div key={index}>
                <div
                  className={`py-2 px-4 bg-gray_f9 rounded-lg flex items-center justify-between dark:bg-[#192844] ${
                    showError ? "bg-error/10 border border-error" : ""
                  }`}
                >
                  <div className="flex items-center text-body text-dark1 flex-1 overflow-hidden dark:text-gray_f9">
                    <span className="flex-shrink-0">Follow</span>
                    <Link passHref href={twitterUserLink(account)}>
                      <a
                        className="text-[#47679A] underline mx-1 text-ellipsis overflow-hidden dark:text-[#87B5FF]"
                        target="_black"
                      >
                        @{account}
                      </a>
                    </Link>
                    <span className="flex-shrink-0">on Twitter</span>
                  </div>
                  {/* <CheckIcon className={`flex-shrink-0 ml-2 ${accountVerified ? 'fill-green' : ''}`}></CheckIcon> */}
                </div>
                <div
                  className={`text-error text-sm mt-1 ${
                    showError ? "flex" : "hidden"
                  }`}
                >
                  This is required
                </div>
              </div>
            );
          })}
        {form.values.needLike && form.values.likeLink && (
          <div>
            <div
              className={`py-2 px-4 bg-gray_f9 rounded-lg flex items-center justify-between dark:bg-[#192844] ${
                showLikeError ? "bg-error/10 border border-error" : ""
              }`}
            >
              <div className="flex items-center text-body text-dark1 dark:text-gray_f9">
                Like this
                <Link passHref href={form.values.likeLink as string}>
                  <a
                    className="text-[#47679A] underline mx-1 dark:text-[#87B5FF]"
                    target="_black"
                  >
                    tweet
                  </a>
                </Link>
              </div>
              {/* <CheckIcon className={`${likeLinkVerified ? 'fill-green' : ''}`}></CheckIcon> */}
            </div>
            <div
              className={`text-error text-sm mt-1 ${
                showLikeError ? "flex" : "hidden"
              }`}
            >
              This is required
            </div>
          </div>
        )}
        {form.values.needRetweet && form.values.retweetLink && (
          <div>
            <div
              className={`py-2 px-4 bg-gray_f9 rounded-lg flex items-center justify-between dark:bg-[#192844] ${
                showRetweetError ? "bg-error/10 border border-error" : ""
              }`}
            >
              <div className="flex items-center text-body text-dark1 dark:text-gray_f9">
                Retweet this
                <Link passHref href={form.values.retweetLink as string}>
                  <a
                    className="text-[#47679A] underline mx-1 dark:text-[#87B5FF]"
                    target="_black"
                  >
                    tweet
                  </a>
                </Link>
              </div>
              {/* <CheckIcon className={`${retweetLinkVerified ? 'fill-green' : ''}`}></CheckIcon> */}
            </div>
            <div
              className={`text-error text-sm mt-1 ${
                showRetweetError ? "flex" : "hidden"
              }`}
            >
              This is required
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TwitterVerify;
